import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BetterBuysConstants } from '@app/better-buys/constants/better-buys-constants.enum';
import { BetterBuysService } from '@app/better-buys/services/better-buys.service';
import { ListsService } from '@app/lists/services/lists.service';
import {
  IonInput,
  MenuController,
  ViewDidEnter,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { BetterBuyDeclineEvent } from '@shared/models/better-buy-card.model';
import {
  DownloadListOptions,
  ListConstants,
  ListViewMode,
  ListViewService,
  LIST_VIEW_ACTIONS,
  ListSearchSortFilterOptions,
  PmeFilterOptions,
} from '@usf/ngrx-list';
import { LoadingState } from '@usf/ngrx-product';
import {
  Observable,
  Subscription,
  combineLatest,
  firstValueFrom,
  of,
  timer,
} from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { LoginService } from 'src/app/login/services';
import { Crumb } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  ListTypes,
  UsfListDetailManagementHeights,
} from '../../../shared/constants/lists-constants';
import { PageTitle } from '../../../shared/constants/page-titles.enum';
import { FEATURES } from '../../../shared/constants/splitio-features';
import { UsfProductCardModeEnum } from '../../../shared/constants/usf-product-card-mode.enum';
import { Product } from '../../../shared/models/product.model';
import {
  EllipsisClickData,
  ProductClickData,
  ProductSelectionData,
} from '../../../shared/models/usf-product-card.model';
import { ListHotkeyService } from '../../../shared/services/hotkey/list-hotkey.service';
import { ListProductCardService } from '../../../shared/services/product-card/list-product-card.service';
import { ListAnalyticsHelperService } from '../../services/list-analytics-helper.service';
import { ListsOverlayService } from '../../services/lists-overlay.service';
import {
  ItemTypes,
  ProductRow,
} from '../../shared/list-detail-management/model/list-detail-management-view.model';
import { ListDetailManagementService } from '../../shared/list-detail-management/services/list-detail-management.service';
import { RecentlyPurchasedViewModel } from './model/recently-purchased-view.model';
import { RecentlyPurchasedService } from './services/recently-purchased.service';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { ListDownloadContainerComponent } from '@app/lists/shared/list-download-container/list-download-container.component';
import { ListPrintContainerComponent } from '@app/lists/shared/list-print-container/list-print-container.component';
import { ClientListViewService } from '@app/lists/services/client-list-view.service';
import { EasyPricingUpdate, EasyPricingService } from '@usf/ngrx-pricing';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-recently-purchased',
  templateUrl: './recently-purchased.page.html',
  styleUrls: [
    '../lists-pages-common-styles.scss',
    './recently-purchased.page.scss',
  ],
})
export class RecentlyPurchasedPage
  implements ViewWillEnter, ViewDidEnter, ViewWillLeave
{
  // Children components
  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('priceModeSidebar') sidebar: ElementRef;
  @ViewChild(ListDownloadContainerComponent)
  listDownloadModal: ListDownloadContainerComponent;
  @ViewChild(ListPrintContainerComponent)
  listPrintModal: ListPrintContainerComponent;

  // Constants
  mode: UsfProductCardModeEnum = UsfProductCardModeEnum.recentlyPurchased;
  readonly itemTypes = ItemTypes;
  readonly listTypes = ListTypes;
  readonly ListViewMode = ListViewMode;
  listName: string;
  copyListEnabled: boolean;

  // Component variables
  vm: RecentlyPurchasedViewModel;
  sidebarTopPosition: number;
  searchKey = '';
  breadcrumbs: Crumb[];
  showTopButton = false;
  isSelectedListLoaded$: Observable<boolean>;
  isGoingToPDP = false;
  // Subscriptions
  recentPurchaseSub$: Subscription = new Subscription();
  virtualScrollSub$: Subscription = new Subscription();
  currentQuantityInput: IonInput = undefined;
  recentlyPurchasedDeleteFlag$: Observable<boolean>;

  // Next week date range for PME toggle
  saturdayDate: string;
  fridayDate: string;
  isFriday$: Observable<boolean>;

  constructor(
    private router: Router,
    readonly panAppState: PanAppState,
    private hotKeyService: ListHotkeyService,
    protected loginService: LoginService,
    readonly platformService: PlatformService,
    public translateService: TranslateService,
    private listAnalyticsHelperService: ListAnalyticsHelperService,
    private listDetailManagementService: ListDetailManagementService,
    private listProductCardService: ListProductCardService,
    public listsOverlayService: ListsOverlayService,
    public listsService: ListsService,
    private recentlyPurchasedService: RecentlyPurchasedService,
    private betterBuysService: BetterBuysService,
    readonly addToListModalService: AddToListModalService,
    public readonly clientListViewService: ClientListViewService,
    private readonly listViewService: ListViewService,
    private readonly easyPricingService: EasyPricingService,
    private renderer: Renderer2,
    private store: Store,
    private readonly menuController: MenuController,
  ) {
    this.recentlyPurchasedDeleteFlag$ =
      this.recentlyPurchasedService.recentlyPurchasedDelete$;
  }

  ionViewWillEnter() {
    this.currentQuantityInput = undefined;
    this.updateListForUserPreferences();
    this.setPageTitle();
    this.setVirtualScrollSub();
    this.setBreadcrumbs();
    this.getViewModel();
    this.resetSearchSortAndFilter();
    this.listName = this.translateService.instant(PageTitle.recentlyPurchased);
    this.panAppState
      .featureEnabled(FEATURES.split_global_copy_list.name)
      .then(isEnabled => (this.copyListEnabled = isEnabled));
    this.isGoingToPDP = false;
    this.isSelectedListLoaded$ =
      this.clientListViewService.isSelectedListLoaded$;
    this.saturdayDate = this.listDetailManagementService.getNextSaturday();
    this.fridayDate = this.listDetailManagementService.getNextFriday();
    this.isFriday$ = timer(0, 60000).pipe(map(() => new Date().getDay() === 5));

    //Ensure toggle is reset if it was on
    if (this.vm?.selectedListViewModel?.fridayToggleOn) {
      this.listDetailManagementService.updateToggleState(
        this.vm?.selectedListViewModel?.fridayToggleOn ?? false,
      );
    }
  }

  updateListForUserPreferences() {
    this.listDetailManagementService.updateRecentlyViewedUserPreferences(
      'recentlyPurchased',
    );
  }

  setPageTitle() {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.lists),
    );
  }

  /**
   * Sets up the virtual scroll subscription to handle sidebar positioning during scrolling.
   * Initializes the base position for the sidebar based on the platform type.
   */
  setVirtualScrollSub() {
    this.virtualScrollSub$ = this.virtualScroll
      ?.elementScrolled()
      ?.subscribe(scrollEvent => {
        this.showTopButton =
          this.virtualScroll.measureScrollOffset('top') > 5 ? true : false;
        if (
          this.platformService.platformType !==
          this.platformService.platformEnum.mobile
        ) {
          const event = scrollEvent as any;
          this.adjustPriceModeSidebarTop(event);
        }
      });
  }

  /**
   * Adjusts the sidebar's top position based on scroll position and platform type.
   */
  adjustPriceModeSidebarTop(event: any) {
    let newTopPosition: number;

    if (
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop
    ) {
      newTopPosition = 332 - event?.target?.scrollTop;
      if (newTopPosition < 20) {
        newTopPosition = 20;
      }
    } else if (
      this.platformService.platformType ===
      this.platformService.platformEnum.tablet
    ) {
      newTopPosition = 255 - event?.target?.scrollTop;
      if (newTopPosition < 15) {
        newTopPosition = 15;
      }
      this.setPriceModeSidebarTopStyle(newTopPosition);
      return;
    }

    this.setPriceModeSidebarTopStyle(newTopPosition);
  }

  /**
   * Updates the sidebar's top position in the DOM if the position has changed.
   * Uses the renderer to safely update the style property.
   */
  setPriceModeSidebarTopStyle(newTopPosition: number) {
    if (
      newTopPosition !== this.sidebarTopPosition &&
      this.sidebar?.nativeElement
    ) {
      this.sidebarTopPosition = newTopPosition;
      this.renderer?.setStyle(
        this.sidebar.nativeElement,
        this.translateService.instant('i18n.common.measurements.top'),
        this.sidebarTopPosition.toString() +
          this.translateService.instant('i18n.common.measurements.px'),
      );
    }
  }

  /**
   * Sets the initial position of the sidebar based on the platform type.
   * Used when first entering pricing mode or when the component initializes.
   */
  setInitialSidebarPosition() {
    const initialTopPosition =
      this.platformService?.platformType ===
      this.platformService.platformEnum.desktop
        ? UsfListDetailManagementHeights.groupsSidebarBaseTopPositionDesktop
        : UsfListDetailManagementHeights.groupsSidebarBaseTopPositionTablet;

    this.sidebarTopPosition = initialTopPosition;

    if (this.sidebar?.nativeElement) {
      this.setPriceModeSidebarTopStyle(initialTopPosition);
    }
  }

  /**
   * Adjusts the sidebar position based on the current scroll position when switching to pricing mode.
   * This is used as a one-time adjustment when the mode changes.
   */
  adjustSidebarPositionForModeChange() {
    if (this.virtualScroll) {
      const currentScrollTop = this.virtualScroll.measureScrollOffset('top');
      const event = { target: { scrollTop: currentScrollTop } };
      this.adjustPriceModeSidebarTop(event);
    }
  }

  handlePricingModeSidebarPosition() {
    this.setInitialSidebarPosition();

    const currentScrollTop =
      this.virtualScroll?.measureScrollOffset('top') || 0;

    // Then wait for the next tick to ensure DOM is updated
    requestAnimationFrame(() => {
      // Adjust with the scroll position we captured
      const event = { target: { scrollTop: currentScrollTop } };
      this.adjustPriceModeSidebarTop(event);
    });
  }

  setBreadcrumbs() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myLists',
        url: '/desktop/lists',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.recentlyPurchased',
        url: '',
        active: true,
      },
    ];
  }

  getViewModel() {
    this.recentPurchaseSub$ = this.recentlyPurchasedService
      .selectRpVm$()
      .subscribe(vm => {
        this.setVmAndHotkeyData(vm);
        this.dispatchEndSearchAction();
      });
  }

  setVmAndHotkeyData(viewModel: RecentlyPurchasedViewModel) {
    this.vm = viewModel;
    this.hotKeyService.setItemHeights(viewModel?.itemHeights);
    this.hotKeyService.setProductCardIds(viewModel?.productCardIds);
    this.hotKeyService.setListId(ListTypes.recentlyPurchased);
    this.hotKeyService.setListViewMode(
      viewModel?.selectedListViewModel?.selectedListViewMode,
    );
  }

  dispatchEndSearchAction() {
    if (this.searchKey.length > 0) {
      this.recentlyPurchasedService.searchInRecentPurchase(
        this.searchKey,
        false,
        true,
      );
    }
  }

  search() {
    this.recentlyPurchasedService.searchInRecentPurchase(
      this.searchKey,
      true,
      false,
    );
  }

  async resetSearch() {
    this.clearFocus();
    setTimeout(this.searchTimeout, 50);
  }

  searchTimeout = () => {
    this.searchKey = '';
    this.search();
  };

  trackItem(index: any, item: any) {
    return item ? index : undefined;
  }

  ionViewDidEnter() {
    window.dispatchEvent(new Event('resize'));
    this.trackPageLoad();
  }

  trackPageLoad() {
    this.recentlyPurchasedService
      .selectRpVm$()
      .pipe(
        filter(
          (viewModel: RecentlyPurchasedViewModel) =>
            viewModel.productLoadingState === LoadingState.loaded ||
            viewModel.productLoadingState === LoadingState.error,
        ),
        take(1),
      )
      .subscribe((viewModel: RecentlyPurchasedViewModel) => {
        if (viewModel.productLoadingState === LoadingState.loaded) {
          this.handleSuccessfulPageLoad(viewModel);
        }
        if (viewModel.productLoadingState === LoadingState.error) {
          this.handleErrorForPageLoad(viewModel);
        }
      });
  }

  handleSuccessfulPageLoad(viewModel: RecentlyPurchasedViewModel) {
    this.listAnalyticsHelperService.trackListDetailPageLoad(
      ListConstants.recentlyPurchased.toLowerCase(),
      viewModel.items,
    );
    this.listDetailManagementService.displayListDetailsSuccessTrace(
      ListConstants.recentlyPurchased,
      '0',
      viewModel.totalItemCount,
    );
  }

  handleErrorForPageLoad(viewModel: RecentlyPurchasedViewModel) {
    this.listsOverlayService.showListLoadingErrorToast();
    this.listDetailManagementService.displayListDetailsFailureTrace(
      ListConstants.recentlyPurchased,
      '0',
      viewModel.items.length,
    );
    this.routeTo('/lists');
  }

  ionViewWillLeave() {
    this.handleSubscriptions();
    this.resetSearchSortAndFilter();
    if (!this.isGoingToPDP) {
      this.resetToDefaultSelectionState();
    }
  }

  handleSubscriptions() {
    this.currentQuantityInput = undefined;
    this.recentPurchaseSub$.unsubscribe();
    this.virtualScrollSub$.unsubscribe();
  }

  resetSearchSortAndFilter() {
    this.searchKey = '';
    this.recentlyPurchasedService.sortAndFilterInRecentPurchase(
      ListConstants.date,
      ListConstants.allGroups,
      [],
      true,
    );
    this.recentlyPurchasedService.searchInRecentPurchase('', true, false);
    this.listDetailManagementService.setPmeSortFilterOptions(
      '',
      ListConstants.date,
      ListConstants.allGroups,
      [],
      ListConstants.asc,
    );
  }

  goToTop() {
    this.platformService?.platformType !==
    this.platformService.platformEnum.mobile
      ? this.virtualScroll.scrollToIndex(0)
      : this.virtualScroll.scrollToIndex(0, 'smooth');
  }

  quantityFocusHandler(input: IonInput) {
    this.currentQuantityInput = input;
  }

  clearFocus() {
    if (this.currentQuantityInput) {
      this.currentQuantityInput.getInputElement().then(element => {
        element.blur();
      });
      this.currentQuantityInput = undefined;
    }
  }

  async changeQuantityHandler(event: any) {
    this.listAnalyticsHelperService.recentPurchaseAddToOrder(
      event.quantityUpdate,
      event.product,
      event.alternative,
    );
  }

  async openSortAndFilterListModal() {
    const isPricingMode =
      this.vm?.selectedListViewModel?.selectedListViewMode ===
      ListViewMode.pricing;
    const sortFilterOptions = isPricingMode
      ? this.vm?.pmeSortFilterOptions
      : this.vm?.searchSortFilterOptionsInRecentPurchase;

    const modal = await this.listsOverlayService.openSortAndFilterListModal(
      [], // groupNames
      this.vm?.productClasses,
      sortFilterOptions?.sortType,
      sortFilterOptions?.filters,
      sortFilterOptions?.groupToSort,
      true, // isRecentPurchase
      false, // isMasterList
      sortFilterOptions?.week52Filter, //52weekFilter
    );

    modal.onDidDismiss().then(result => {
      if (result?.data) {
        const sortData = result as any;
        if (isPricingMode) {
          this.listDetailManagementService.setPmeSortFilterOptions(
            sortFilterOptions?.searchKey || '',
            sortData?.data.sortMethod,
            sortData?.data.groupToSort,
            sortData?.data.filters,
            sortFilterOptions?.sortDirection || ListConstants.asc,
          );
        } else {
          this.recentlyPurchasedService.sortAndFilterInRecentPurchase(
            sortData?.data.sortMethod,
            sortData?.data.groupToSort,
            sortData?.data.filters,
            sortData?.data.week52Filter,
          );

          this.listDetailManagementService.updateSortAndFilterUserPreferences(
            ListConstants.recentlyPurchased,
            sortData?.data.sortMethod,
            sortData?.data.groupToSort,
            sortData?.data.filters,
          );
        }
      }
    });
  }

  routeTo(path: string) {
    this.router.navigate([path]);
  }

  async routeToPDP(productClickData: ProductClickData) {
    this.isGoingToPDP = true;
    if (productClickData.betterBuyCard) {
      this.listProductCardService.routeToPDPBetterBuyCard(
        productClickData.productNumber,
        productClickData.currentProduct,
      );
    } else {
      this.listProductCardService.routeToPDP(
        productClickData.productNumber,
        productClickData.isQuickView,
      );
    }
  }

  async openProductCardEllipsis(ellipsisClickData: EllipsisClickData) {
    const recentlyPurchasedDeleteFlag = await firstValueFrom(
      this.recentlyPurchasedDeleteFlag$,
    );
    const buttons =
      await this.listProductCardService.constructEllipsisButtons(
        ellipsisClickData,
      );
    if (recentlyPurchasedDeleteFlag) {
      buttons.push(
        this.listProductCardService.getRecentlyPurchasedDeleteProductBtn(
          ellipsisClickData,
        ),
      );
    }
    this.listProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  async clickPromotions(event: EllipsisClickData) {
    this.listProductCardService.openQuickViewModal(event, 'productStatus');
  }

  async openProductStatus(ellipsisClickData: EllipsisClickData) {
    this.listProductCardService.showProductStatuses(ellipsisClickData);
  }

  handleBetterBuyDeclineClick(betterBuyDeclineEvent: BetterBuyDeclineEvent) {
    this.betterBuysService.openDeclineModal(betterBuyDeclineEvent);
  }

  acceptSwap(product: Product) {
    this.betterBuysService.openAcceptSwapModal(
      product,
      false,
      BetterBuysConstants.listsCapability,
    );
  }

  closeDownloadListModal() {
    this.listsOverlayService.closeDownloadListModal();
  }

  openListOptions(event: Event) {
    this.recentlyPurchasedService.openListOptions(
      event,
      this.vm?.selectedCustomer,
      this.vm?.showEllipsis,
      this.listDownloadModal,
    );
  }

  downloadList(options: DownloadListOptions) {
    this.listsOverlayService.closeDownloadListModal();
    this.recentlyPurchasedService.downloadList(options);
  }

  async openDownloadPrintModal(download: boolean) {
    const listName = 'Recently Purchased';
    if (download) {
      this.listsOverlayService.openDownloadListModal(
        0,
        ListTypes.recentlyPurchased,
        listName,
        this.listDownloadModal,
      );
    } else {
      this.listsOverlayService.openPrintListModal(
        0,
        ListTypes.recentlyPurchased,
        listName,
        this.vm.selectedCustomer.customerNumber,
        '',
        this.listPrintModal,
      );
    }
  }

  handleOpenSortAndFilterListModal(viewMode: ListViewMode) {
    if (viewMode === ListViewMode.pricing) {
      this.openSortFilterMenu();
    } else {
      this.openSortAndFilterListModal();
    }
  }

  handleClearSortAndFilterListModalEmitter(viewMode: ListViewMode) {
    if (viewMode === ListViewMode.pricing) {
      this.listDetailManagementService.setPmeSortFilterOptions(
        '',
        ListConstants.date,
        ListConstants.allGroups,
        [],
        ListConstants.asc,
      );
    } else if (viewMode === ListViewMode.ordering) {
      this.recentlyPurchasedService.sortAndFilterInRecentPurchase(
        ListConstants.date,
        ListConstants.allGroups,
        [],
        true,
      );

      this.listDetailManagementService.updateSortAndFilterUserPreferences(
        ListConstants.recentlyPurchased,
        ListConstants.date,
        ListConstants.allGroups,
        [],
      );
    }
  }

  selectAProduct = (selection: ProductSelectionData) => {
    this.recentlyPurchasedService.selectAProduct(selection);
  };

  toggleAddToList = () => {
    this.recentlyPurchasedService.toggleAddToList(!this.vm.isSelected);
    this.recentlyPurchasedService.toggleSelectionsForAllRecentPurchase(false);
  };

  resetToDefaultSelectionState = () => {
    this.recentlyPurchasedService.toggleAddToList(false);
    this.recentlyPurchasedService.toggleSelectionsForAllRecentPurchase(false);
  };

  getFilteredProducts = () => {
    return this.vm.items
      .filter(item => {
        return item.itemType === ItemTypes.product;
      })
      .map(product => {
        return (product as ProductRow).productNumber;
      });
  };

  selectAllProducts() {
    this.recentlyPurchasedService.toggleSelectionForAllFilteredRecentPurchases(
      true,
      this.getFilteredProducts(),
    );
  }

  deselectAllProducts() {
    this.recentlyPurchasedService.toggleSelectionForAllFilteredRecentPurchases(
      false,
      this.getFilteredProducts(),
    );
  }

  openAddToList = async () => {
    const selectedProducts = await firstValueFrom(
      this.recentlyPurchasedService.getSelectedProducts(),
    );
    this.addToListModalService.openModal(selectedProducts, false, false);
  };

  closeAndDeatachAddToListModal = () => {
    this.addToListModalService.closeModal();
  };

  productConversionEmitter(product: Product) {
    this.listProductCardService.handleProductConversionClick(product, false);
  }

  productDetailsButtonClick() {
    if (this.vm?.selectedProduct) {
      this.listProductCardService.routeToPDP(
        this.vm.selectedProduct.productNumber,
        false,
      );
    }
  }

  deleteSingleProduct = () => {
    this.recentlyPurchasedService.deleteSingleProduct();
    this.listsOverlayService.closeRecentlyPurchasedDeleteModal();
  };

  handleListViewModeChange(event: any) {
    const listViewMode = event.detail.value as ListViewMode;
    this.listViewService.changeViewMode(listViewMode, false);

    if (
      listViewMode === ListViewMode.ordering &&
      this.vm?.selectedListViewModel?.fridayToggleOn
    ) {
      this.listDetailManagementService.updateToggleState(
        this.vm?.selectedListViewModel?.fridayToggleOn ?? false,
      );
    }

    // Handle sidebar position when switching to pricing mode
    if (listViewMode === ListViewMode.pricing) {
      this.handlePricingModeSidebarPosition();
    }
  }

  emitPriceChanged(update: EasyPricingUpdate) {
    this.easyPricingService.updateEasyPricing(
      {
        ...update,
        isFridayPricing:
          this.vm?.selectedListViewModel?.fridayToggleOn ?? false,
      },
      this.vm?.selectedCustomerDivision?.tandemSystem,
    );
  }

  handleFridayToggleChange(fridayPricingToggleIsOn: boolean) {
    this.listDetailManagementService.updateToggleState(
      fridayPricingToggleIsOn ?? false,
    );
    setTimeout(() => {
      this.listDetailManagementService.updatePricingWithFridayOption(
        this.vm?.selectedListViewModel?.productNumbers,
        fridayPricingToggleIsOn,
        this.vm?.selectedListViewModel?.selectedListType,
      );
    }, 100);
  }

  routeToPDPAndClosePmeModal(productNumber: number) {
    this.closeMobilePmeModal();
    setTimeout(() => {
      this.routeToPDP({ productNumber, isQuickView: false });
    }, 100);
  }

  closeMobilePmeModal() {
    this.listDetailManagementService.setSelectedListItem(null);
  }

  handlePmeProductClick(item: ProductRow) {
    if (
      this.vm?.selectedListViewModel?.selectedListViewMode ===
      ListViewMode.pricing
    ) {
      const trackingData =
        this.listAnalyticsHelperService.createProductClickTrackingData(item);
      this.listDetailManagementService.setSelectedListItem(
        item.productNumber,
        trackingData,
      );
    }
  }

  private updatePmeSortFilterOptions(
    filters: string[],
    searchKey?: string,
    sortType?: string,
    groupToSort?: string,
    sortDirection?: string,
  ) {
    // Sort filters in the same order as PME filter buttons
    const sortedFilters = [...filters].sort((a, b) => {
      const order = {
        [PmeFilterOptions.showAllPriceChange]: 0,
        [PmeFilterOptions.notInOptimizedRange]: 1,
        [PmeFilterOptions.showAllMarketChange]: 2,
      };
      return (order[a] ?? 999) - (order[b] ?? 999);
    });

    // Update the PME sort and filter options while preserving other options
    this.listDetailManagementService.setPmeSortFilterOptions(
      searchKey || this.vm?.pmeSortFilterOptions?.searchKey || '',
      sortType ||
        this.vm?.pmeSortFilterOptions?.sortType ||
        ListConstants.groupLine,
      groupToSort ||
        this.vm?.pmeSortFilterOptions?.groupToSort ||
        ListConstants.allGroups,
      sortedFilters,
      sortDirection ||
        this.vm?.pmeSortFilterOptions?.sortDirection ||
        ListConstants.asc,
    );
  }

  handleFiltersChange(filters: string[]) {
    this.updatePmeSortFilterOptions(filters);
  }

  async savePmeSortAndFilter(sortFilterOptions: ListSearchSortFilterOptions) {
    this.updatePmeSortFilterOptions(
      sortFilterOptions.filters,
      sortFilterOptions.searchKey,
      sortFilterOptions.sortType,
      sortFilterOptions.groupToSort,
      sortFilterOptions.sortDirection,
    );
  }

  async openSortFilterMenu() {
    await this.menuController.enable(true, 'recently-purchased-pme-menu');
    await this.menuController.open('recently-purchased-pme-menu');
  }

  async closeSortFilterMenu() {
    await this.menuController.close('recently-purchased-pme-menu');
  }

  handleCloseManualApproval(productNumber: number) {
    this.easyPricingService.resetProcessingStateForProduct(productNumber);
  }

  selectFirstProductForPricing() {
    if (
      this.platformService.platformType ===
        this.platformService.platformEnum.mobile ||
      this.vm?.selectedListViewModel?.selectedListViewMode !==
        ListViewMode.pricing
    ) {
      return;
    }

    const firstProduct = this.vm?.items?.find(
      item => item.itemType === 'product',
    ) as ProductRow;
    if (firstProduct) {
      this.handlePmeProductClick(firstProduct);
    }
  }
}
